import * as THREE from 'three/webgpu';
import type { GearTier } from '../shared/rpg';

export interface KaelTierState {
  readonly tier: GearTier;
  readonly wanted: GearTier;
  readonly loaded: readonly GearTier[];
}

/** 이름으로 연결한다. 같은 65본이라도 GLB의 joints 배열 순서는 같다고 가정하지 않는다. */
export function remapTierSkin(geometry: THREE.BufferGeometry, sourceNames: readonly string[], targetNames: readonly string[]): THREE.BufferGeometry {
  const target = new Map(targetNames.map((name, i) => [name, i]));
  if (target.size !== targetNames.length) throw new Error('T1 본 이름 중복');
  const mapping = sourceNames.map(name => {
    const index = target.get(name);
    if (index === undefined) throw new Error('T1에 없는 단계 본: ' + name);
    return index;
  });
  const src = geometry.getAttribute('skinIndex');
  if (!src || src.itemSize !== 4) throw new Error('단계 skinIndex 형식 오류');
  const out = new Uint16Array(src.count * 4);
  for (let i = 0; i < src.count; i++) for (let k = 0; k < 4; k++) {
    const index = mapping[src.getComponent(i, k)];
    if (index === undefined) throw new Error('단계 skinIndex 범위 오류');
    out[i * 4 + k] = index;
  }
  const result = geometry.clone();
  result.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(out, 4));
  return result;
}

/** meshopt 양자화의 복원 배율·중심은 inverse-bind에도 들어간다. 같은 관절 이름만으로는 충분하지 않다. */
export function rebindTierGeometry(source: THREE.SkinnedMesh, target: THREE.SkinnedMesh): THREE.BufferGeometry {
  const geometry = remapTierSkin(source.geometry, source.skeleton.bones.map(b => b.name), target.skeleton.bones.map(b => b.name));
  const correction = new THREE.Matrix4(), candidate = new THREE.Matrix4();
  let residual = 0;
  try {
    for (let i = 0; i < source.skeleton.bones.length; i++) {
      const index = target.skeleton.bones.findIndex(b => b.name === source.skeleton.bones[i].name);
      candidate.copy(target.bindMatrix).invert()
        .multiply(target.skeleton.boneInverses[index].clone().invert())
        .multiply(source.skeleton.boneInverses[i]).multiply(source.bindMatrix);
      if (i === 0) correction.copy(candidate);
      else for (let k = 0; k < 16; k++) residual = Math.max(residual, Math.abs(candidate.elements[k] - correction.elements[k]));
    }
    // 공통 양자화 변환과 다른 관절 rest 변형을 구분한다. 불일치를 정점마다 임의로 보정하지 않는다.
    if (!Number.isFinite(residual) || residual > 1e-4) throw new Error('단계 inverse-bind 공통 변환 불일치: ' + residual);
    // 정규화 정수 속성에 바로 행렬을 곱하면 ±1 밖의 값이 잘린다.
    for (const name of ['position', 'normal', 'tangent']) {
      const src = geometry.getAttribute(name);
      if (!src) continue;
      const data = new Float32Array(src.count * src.itemSize);
      for (let i = 0; i < src.count; i++) for (let k = 0; k < src.itemSize; k++) data[i * src.itemSize + k] = src.getComponent(i, k);
      geometry.setAttribute(name, new THREE.Float32BufferAttribute(data, src.itemSize));
    }
    geometry.applyMatrix4(correction);
    geometry.userData.tierBinding = { correction: correction.toArray(), inverseBindResidual: residual };
    return geometry;
  } catch (error) { geometry.dispose(); throw error; }
}

/** 모델 교체도 감지한다. 같은 단계의 프레임에서는 할당·비동기 호출이 없다. */
export class ArmorTierBinding {
  private model: { setTier(tier: GearTier): unknown } | null = null;
  private tier: GearTier | 0 = 0;
  update(model: { setTier(tier: GearTier): unknown }, tier: GearTier): void {
    if (this.model === model && this.tier === tier) return;
    this.model = model; this.tier = tier;
    model.setTier(tier);
  }
}

/**
 * 장착 무기 단계별 칼날 빛(ECONOMY 11절). edge는 가장자리 프레넬에 곱해 더하는 색(밝기 포함), glow는 텍스처 자체발광 계수(색별).
 * blade는 가드 위 칼날 몸통에 더하는 단계 색(밝기 포함). 2026-10-19 정본 캡처에서 가장자리만으로는 단계가 구별되지 않아 추가했다.
 * T1은 단계 도입 전과 같은 값(흰빛 0.75, 자체발광 0.3, 칼날 0)이다. 새 재질 없이 이 값을 uniform에 넣어 바꾼다.
 */
type V3 = readonly [number, number, number];
/** tint는 칼날 몸통이 받는 빛(확산색)에 곱하는 색. 밝은 햇빛에서도 단계 색이 남게 한다. T1은 [1,1,1](변화 없음) */
export const SWORD_TIER_LOOK: Readonly<Record<GearTier, { readonly edge: V3; readonly glow: V3; readonly blade: V3; readonly tint: V3 }>> = {
  1: { edge: [1.0 * 0.75, 0.93 * 0.75, 0.86 * 0.75], glow: [0.3, 0.3, 0.3], blade: [0, 0, 0], tint: [1, 1, 1] },
  2: { edge: [0.6 * 1.4, 0.85 * 1.4, 1.0 * 1.4], glow: [0.28, 0.32, 0.4], blade: [0.08, 0.26, 0.66], tint: [0.62, 0.8, 1.0] },
  3: { edge: [1.0 * 1.6, 0.5 * 1.6, 0.12 * 1.6], glow: [0.5, 0.34, 0.2], blade: [0.77, 0.26, 0.03], tint: [1.0, 0.78, 0.5] },
};

/** 무기 단계가 바뀔 때(또는 모델이 바뀔 때)만 setWeaponTier를 호출한다. 같은 단계의 프레임에서는 할당·호출이 없다. */
export class WeaponTierBinding {
  private model: { setWeaponTier(tier: GearTier): unknown } | null = null;
  private tier: GearTier | 0 = 0;
  update(model: { setWeaponTier(tier: GearTier): unknown }, tier: GearTier): void {
    if (this.model === model && this.tier === tier) return;
    this.model = model; this.tier = tier;
    model.setWeaponTier(tier);
  }
}

/** 지연 중에는 현재 몸 유지, 완료 순서와 무관하게 마지막 요청만 표시한다. */
export class KaelTierCache<T> {
  private readonly values = new Map<GearTier, T>();
  private readonly pending = new Map<GearTier, Promise<void>>();
  private tier: GearTier = 1;
  private wanted: GearTier = 1;
  private disposed = false;
  constructor(base: T, private readonly load: (tier: GearTier) => Promise<T>,
    private readonly apply: (value: T) => void, private readonly release: (value: T) => void,
    private readonly warn: (error: unknown) => void = error => console.warn('카엘 단계 로드 실패; T1으로 복귀합니다.', error)) {
    this.values.set(1, base);
  }
  get state(): KaelTierState {
    return Object.freeze({ tier: this.tier, wanted: this.wanted, loaded: Object.freeze([...this.values.keys()].sort()) });
  }
  setTier(tier: GearTier): Promise<void> {
    if (this.disposed) return Promise.resolve();
    this.wanted = tier;
    const cached = this.values.get(tier);
    if (cached) {
      if (this.tier !== tier) { this.apply(cached); this.tier = tier; }
      return Promise.resolve();
    }
    const pending = this.pending.get(tier);
    if (pending) return pending;
    const request = this.load(tier).then(value => {
      if (this.disposed) { this.release(value); return; }
      this.values.set(tier, value);
      if (this.wanted === tier) { this.apply(value); this.tier = tier; }
    }).catch(error => {
      if (this.disposed) return;
      this.warn(error);
      if (this.wanted === tier) { this.apply(this.values.get(1)!); this.tier = 1; }
    }).finally(() => this.pending.delete(tier));
    this.pending.set(tier, request);
    return request;
  }
  dispose(): void {
    this.disposed = true;
    for (const value of this.values.values()) this.release(value);
    this.values.clear();
  }
}
